import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { Button, IconButton } from './Button';

// 모달이 겹쳐도 마지막 한 겹이 닫힐 때까지 body 잠금을 유지한다. 첫 모달이 열리기 전
// inline 값을 복원하므로, 다른 화면이 이미 `clip` 등을 쓰고 있던 경우도 덮어쓰지 않는다.
let bodyScrollLockCount = 0;
let bodyOverflowBeforeLock = '';

const lockBodyScroll = () => {
  if (bodyScrollLockCount === 0) bodyOverflowBeforeLock = document.body.style.overflow;
  bodyScrollLockCount += 1;
  document.body.style.overflow = 'hidden';
  let released = false;
  return () => {
    if (released) return;
    released = true;
    bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
    if (bodyScrollLockCount === 0) document.body.style.overflow = bodyOverflowBeforeLock;
  };
};

/**
 * 공통 모달 컴포넌트
 * 재사용 가능한 모달 레이아웃과 기능 제공
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md', // 'sm', 'md', 'lg', 'xl', 'full', 'max', 'content'
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEsc = true,
  footer,
  headerActions,
  className = '',
  bodyClassName = '',
  preventScroll = false,
  inactive = false,
  zIndex = 9999980, // 기본 z-index 추가, 네비게이션바(9999999)보다 낮게 설정
  isMobileView = window.innerWidth < 768, // 모바일 뷰 감지
  animationDuration = 0, // 애니메이션 지속 시간 (ms) - 페이드 효과 제거
  maxHeight = null, // 커스텀 최대 높이 (예: '95vh')
  fixedHeight = false // true이면 높이를 maxHeight로 고정 (컨텐츠 양에 관계없이)
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const mouseDownTargetRef = React.useRef(null); // 드래그 시작점 추적
  const dialogRef = React.useRef(null);
  const restoreFocusRef = React.useRef(null);
  const titleId = React.useId();

  // opener는 내부로 초점을 옮기기 전 layout 단계에서 한 번만 보존한다. StrictMode가
  // effect를 다시 실행해도 이미 보존한 값을 모달 내부 요소로 덮어쓰지 않는다.
  React.useLayoutEffect(() => {
    if (isOpen && restoreFocusRef.current == null) {
      restoreFocusRef.current = document.activeElement;
    }
  }, [isOpen]);

  // 닫기 클릭의 기본 동작까지 끝난 뒤 opener로 복귀해야 제거될 버튼에 초점이 다시 붙지 않는다.
  // 그래서 캡처·진입은 layout, 복귀는 passive cleanup으로 역할을 나눈다.
  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      const prev = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (prev instanceof HTMLElement && document.contains(prev)) prev.focus();
    };
  }, [isOpen]);

  // portal이 실제 DOM에 붙은 바로 그 commit에서 초점을 옮긴다. 상위 모달이 inactive가 되는
  // 프레임과 자식 모달의 초점 이동 사이에 빈 틈이 생기지 않게 layout effect를 쓴다.
  React.useLayoutEffect(() => {
    if (!isOpen || !shouldRender || inactive) return;
    const el = dialogRef.current;
    if (!el || el.contains(document.activeElement)) return;
    const focusable = el.querySelector('input,textarea,select,button,[href],[tabindex]:not([tabindex="-1"])');
    (focusable || el).focus();
  }, [isOpen, shouldRender, inactive]);

  // 초점 가두기 — aria-modal 을 선언하는 이상 Tab 이 뒤 화면으로 새면 안 된다
  const handleDialogKeyDown = (e) => {
    if (inactive) return;
    if (e.key !== 'Tab') return;
    const el = dialogRef.current;
    if (!el) return;
    const nodes = Array.from(el.querySelectorAll('input,textarea,select,button,[href],[tabindex]:not([tabindex="-1"])'))
      .filter((n) => !n.disabled && n.offsetParent !== null);
    if (nodes.length === 0) {
      e.preventDefault();
      el.focus();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // 모달 열기/닫기 애니메이션 처리
  React.useLayoutEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      if (animationDuration === 0) {
        // 애니메이션 없을 때는 즉시 표시 (rAF 스킵하여 플리커 방지)
        setIsVisible(true);
      } else {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      }
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, animationDuration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, animationDuration]);
  // ESC 키 처리
  useEffect(() => {
    if (!isOpen || !closeOnEsc || inactive) return undefined;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, closeOnEsc, inactive]);

  // 스크롤 방지
  useEffect(() => {
    if (!preventScroll || !isOpen) return undefined;
    return lockBodyScroll();
  }, [isOpen, preventScroll]);

  if (!shouldRender) return null;

  const sizeClasses = {
    sm: 'w-[95vw] max-w-[400px]',
    md: 'w-[95vw] max-w-[600px]',
    lg: 'w-[95vw] max-w-[800px]',
    xl: 'w-[95vw] max-w-[1000px]',
    full: 'w-[95vw]',
    max: 'w-[98vw] max-w-[1400px]',
    content: isMobileView
      ? 'w-[95vw] max-w-[95vw]' // 모바일: min-width 제거 → 화면 폭에 맞춰 자동 축소
      : 'w-auto min-w-[600px] max-w-[95vw]' // 데스크톱: 컨텐츠 폭만큼 유동
  };

  const handleBackdropMouseDown = (e) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleBackdropClick = (e) => {
    // mousedown과 click이 모두 backdrop에서 발생한 경우에만 닫기
    // (모달 내부에서 드래그 시작 → 바깥에서 놓기 방지)
    if (!inactive && closeOnBackdropClick && e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  const handleModalContentClick = (e) => {
    // Lexical 편집기 안에서 난 클릭은 멈추지 않는다.
    //
    // 왜 예외가 필요한가: Lexical 은 그림 같은 데코레이터를 **React 포털**로 그리고, React 는
    // 포털 컨테이너(그림을 감싼 span.editor-image)에 리스너를 붙인다. 그 자리는 Lexical 이
    // 자기 명령(CLICK_COMMAND)을 듣는 편집기 루트보다 **DOM 상 아래**다. 그래서 여기서
    // stopPropagation 을 부르면(React 의 그것은 네이티브까지 멈춘다) 이벤트가 편집기 루트에
    // 닿지 못하고, **모달 안에서만 그림 선택이 통째로 죽는다**(리사이즈 핸들·그림 편집 버튼 포함).
    // 실측: 모달 밖 클릭은 루트에 도달(_lexicalHandled=true), 모달 안은 도달 0.
    //
    // 멈추는 목적은 backdrop 의 onClick 이 모달을 닫는 것을 막는 것인데, 그쪽은 이미
    // `e.target === e.currentTarget` 로 스스로를 지킨다(아래 handleBackdropClick).
    // 그래서 편집기 안 클릭만 통과시켜도 닫힘 동작은 그대로다.
    if (e.target?.closest?.('[data-lexical-editor="true"]')) return;
    // backdrop 클릭이 아닌 경우에만 이벤트 버블링 중지
    // 모달 내부 요소들은 정상적으로 이벤트 전파
    e.stopPropagation();
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center transition-all"
      style={{
        zIndex,
        paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
        paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px',
        backgroundColor: isVisible ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
        transitionDuration: `${animationDuration}ms`
      }}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div 
        ref={dialogRef}
        role="dialog"
        aria-modal={inactive ? undefined : 'true'}
        aria-hidden={inactive ? 'true' : undefined}
        inert={inactive ? '' : undefined}
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
        className={`flex flex-col rounded-3xl bg-white shadow-xl outline-none transition-all ${sizeClasses[size]} ${className}`}
        style={{
          height: fixedHeight
            ? (isMobileView
                ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)'
                : (maxHeight || '90vh'))
            : 'auto',
          maxHeight: isMobileView
            ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)'
            : (maxHeight || '90vh'),
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          transitionDuration: `${animationDuration}ms`
        }}
        onClick={handleModalContentClick}
      >
        {/* 헤더 */}
        {(title || showCloseButton || headerActions) && (
          <div className="flex shrink-0 items-center justify-between border-b p-4">
            {title && (
              <h2 id={titleId} className="text-xl font-bold text-gray-800">{title}</h2>
            )}
            <div className="flex items-center">
              {headerActions}
              {showCloseButton && (
                <IconButton
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                  aria-label="닫기"
                >
                  <X size={20} />
                </IconButton>
              )}
            </div>
          </div>
        )}

        {/* 바디 */}
        <div className={`flex-1 p-4 ${bodyClassName || 'overflow-y-auto'}`}>
          {children}
        </div>

        {/* 푸터 */}
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t p-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/**
 * 확인 모달 컴포넌트
 * 예/아니오 선택이 필요한 경우 사용
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = '확인',
  message,
  confirmText = '확인',
  cancelText = '취소',
  confirmVariant = 'primary',
  isLoading = false
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            loading={isLoading}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <p className="text-gray-700">{message}</p>
    </Modal>
  );
};

/**
 * 알림 모달 컴포넌트
 * 단순 정보 전달용 모달
 */
export const AlertModal = ({
  isOpen,
  onClose,
  title = '알림',
  message,
  buttonText = '확인',
  variant = 'primary'
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <Button
          variant={variant}
          onClick={onClose}
        >
          {buttonText}
        </Button>
      }
    >
      <p className="text-gray-700">{message}</p>
    </Modal>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full', 'max', 'content']),
  showCloseButton: PropTypes.bool,
  closeOnBackdropClick: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  footer: PropTypes.node,
  headerActions: PropTypes.node,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  preventScroll: PropTypes.bool,
  inactive: PropTypes.bool,
  zIndex: PropTypes.number,
  maxHeight: PropTypes.string,
  fixedHeight: PropTypes.bool
};

ConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  confirmVariant: PropTypes.string,
  isLoading: PropTypes.bool
};

AlertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  variant: PropTypes.string
};
